import { FACE_EMBEDDING_MODEL } from '@familienbaum/shared';
import { api } from '@/lib/api';
import { landmarksForRegion } from './detect';
import { embedFace } from './embed';

/**
 * Der Nachlauf: allen Markierungen im Archiv ihre Merkmalsreihe nachrechnen.
 *
 * Warum überhaupt ein Nachlauf und nicht beim Hochladen: die Reihen sind erst
 * *nach* diesem Feature entstanden, im Archiv liegen also Markierungen ohne
 * eine. Von Hand gezogene hatten ohnehin nie eine. Und weil gerechnet wird,
 * wo das Bild schon liegt – im Browser –, braucht es jemanden, der einmal
 * hinsieht. Genau das ist dieser Durchlauf.
 *
 * Ein Weg für alles: automatisch gesetzte und von Hand gezogene Markierungen
 * gehen durch dieselbe Mühle. Zwei Wege wären zwei Stellen, an denen die
 * Ausrichtung auseinanderlaufen könnte – und die Ausrichtung entscheidet über
 * die Qualität der Reihe.
 *
 * Pro Bild wird einmal geladen und werden alle Gesichter darauf abgearbeitet.
 * Die API liefert die offenen Markierungen deshalb nach Bild sortiert; bei
 * einem Gruppenfoto mit zwanzig Gesichtern wäre es sonst zwanzigmal dasselbe
 * 2048-px-Bild über die Leitung.
 */

/**
 * Die offenen Markierungen **eines** Bildes vermessen, mit dem Bild, das schon
 * auf dem Schirm ist.
 *
 * Für den Weg direkt nach dem Erkennen: die Rahmen sind gerade entstanden, das
 * Bild liegt entschlüsselt im Speicher, und die Vorschläge sollen ohne Umweg
 * über die Gruppenseite daneben stehen. Derselbe Rechenweg wie im Nachlauf –
 * nur ohne die Runde über `/faces/pending`.
 */
export async function embedRegions(
  image: HTMLImageElement,
  regions: readonly { id: string; x: number; y: number; width: number; height: number }[],
): Promise<{ done: number; skipped: number }> {
  const results: Array<{ regionId: string; embedding: number[] | null }> = [];
  let done = 0;
  let skipped = 0;

  for (const region of regions) {
    const marks = await landmarksForRegion(image, region);
    if (!marks) {
      results.push({ regionId: region.id, embedding: null });
      skipped += 1;
      continue;
    }
    const vector = await embedFace(image, marks);
    results.push({ regionId: region.id, embedding: Array.from(vector) });
    done += 1;
  }

  if (results.length > 0) {
    await api.post('/faces/embeddings', { model: FACE_EMBEDDING_MODEL, regions: results });
  }
  return { done, skipped };
}
